import { useMemo, useState, useEffect } from 'react'
import { useMutation, useQuery } from '@tanstack/react-query'

import Flex from '@/elements/Flex'
import Text from '@/elements/Text'
import FormDatePicker from '@/elements/FormDatePicker'
import Modal from '@/components/Modal'
import { createAlertExportJob, exportAlerts, getAlertExportJob } from '@/api/alert'
import { ApiFileError } from '@/api/request'
import useRoles from '@/hooks/useRoles'
import toast from '@/utils/toast'
import { downloadFromUrl } from '@/utils/download'

type ExportMode = 'organization' | 'myAlerts' | 'individual'

interface ExportModalProps {
  isOpen: boolean
  onClose: () => void
  mode: ExportMode
  /** My Alerts: optional filter by individual. Individual Watchdog: required (route subject). */
  subjectId?: string
}

function ExportModal({ isOpen, onClose, mode, subjectId }: ExportModalProps) {
  const { isSuperAdmin } = useRoles()
  const [includeNonAlerts, setIncludeNonAlerts] = useState(false)
  const [exportFromDate, setExportFromDate] = useState<Date | null>(null)
  const [jobId, setJobId] = useState<string | null>(null)
  const [jobTerminalToastShown, setJobTerminalToastShown] = useState(false)

  useEffect(() => {
    if (isOpen) {
      const d = new Date()
      d.setDate(d.getDate() - 30)
      setExportFromDate(d)
      setJobId(null)
      setJobTerminalToastShown(false)
      if (!isSuperAdmin) {
        setIncludeNonAlerts(false)
      }
    }
  }, [isOpen, isSuperAdmin])

  const params = useMemo(() => {
    const from = exportFromDate ?? (() => {
      const d = new Date()
      d.setDate(d.getDate() - 30)
      return d
    })()
    const days = Math.max(1, Math.ceil((Date.now() - from.getTime()) / 86400000))
    const scope = isSuperAdmin && includeNonAlerts ? 'all' : 'alerts'
    return {
      scope,
      days,
      subjectId: mode === 'organization' ? undefined : subjectId,
      // Individual: subjectId only — do not send assigned=true (backend ignores subjectId in that branch).
      assignedAlerts: mode === 'myAlerts' ? true : undefined
    } as const
  }, [exportFromDate, includeNonAlerts, isSuperAdmin, mode, subjectId])

  const createJobMutation = useMutation({
    mutationFn: () => createAlertExportJob(params),
    onSuccess: id => {
      setJobId(id)
    },
    onError: () => {
      // Fallback to sync export if async endpoint is unavailable in an env.
      exportAlerts(params)
        .then(() => {
          onClose()
          toast.success('Export downloaded')
        })
        .catch((err: unknown) => {
          if (err instanceof ApiFileError) {
            if (err.status === 504) {
              toast.error('Export took too long. Try fewer days or narrower filters and export again.')
              return
            }
            if (err.status === 503) {
              toast.error('Export is temporarily unavailable. Please try again shortly.')
              return
            }
            toast.error(err.message || 'Failed to export alerts')
            return
          }

          const name = (err as { name?: string } | undefined)?.name
          if (name === 'AbortError') {
            toast.error('Export timed out. Please try fewer days or narrower filters.')
            return
          }

          toast.error('Failed to export alerts')
        })
    }
  })

  const jobQuery = useQuery({
    queryKey: ['alertExportJob', jobId],
    queryFn: () => getAlertExportJob(jobId as string),
    enabled: !!jobId,
    refetchInterval: q => {
      const status = q.state.data?.status
      return status === 'pending' || status === 'processing' ? 1500 : false
    }
  })

  useEffect(() => {
    const status = jobQuery.data?.status
    if (!jobId || !status) return

    if (status === 'ready') {
      if (jobTerminalToastShown) return
      const url = jobQuery.data?.downloadUrl
      if (url) {
        downloadFromUrl(url)
        onClose()
        toast.success('Export downloaded')
        setJobTerminalToastShown(true)
      } else {
        toast.error('Export is ready but download link is missing.')
        setJobTerminalToastShown(true)
      }
      return
    }

    if (status === 'failed') {
      if (jobTerminalToastShown) return
      toast.error(jobQuery.data?.errorMessage || 'Export failed. Please try again.')
      setJobTerminalToastShown(true)
    }
  }, [jobId, jobQuery.data, jobTerminalToastShown, onClose])

  const isBusy = createJobMutation.isPending || (!!jobId && (jobQuery.isFetching || jobQuery.data?.status === 'processing' || jobQuery.data?.status === 'pending'))

  const title =
    mode === 'organization'
      ? 'Export organization data'
      : mode === 'myAlerts'
        ? 'Export my alerts'
        : 'Export individual alerts'

  const description =
    mode === 'organization'
      ? 'Exporting all organization alerts will create a'
      : mode === 'myAlerts'
        ? 'Exporting alerts assigned to you will create a'
        : 'Exporting alerts for this individual will create a'

  return (
    <Modal
      title={title}
      isOpen={isOpen}
      onCancel={onClose}
      onSave={() => createJobMutation.mutate()}
      saveLabel='Export'
      cancelLabel='Cancel'
      saveButtonVariant='primary'
      disabled={isBusy}
    >
      <Flex gap={3}>
        <Text color='black'>
          {description}{' '}
          <strong>.XLSX file download</strong> with data from the selected date until today. Large exports may take a minute.
        </Text>
        {!!jobId && (
          <Text color='black-light-40' style={{ fontSize: '0.95rem', margin: 0 }}>
            Export status: <strong>{jobQuery.data?.status || 'starting'}</strong>
          </Text>
        )}
        <FormDatePicker
          name='export-from-date'
          label='From date'
          placeholder='Select start date'
          value={exportFromDate}
          onChange={setExportFromDate}
          maxDate={new Date()}
        />
        {isSuperAdmin && (
          <>
            <div
              style={{
                height: 1,
                minHeight: 1,
                background: 'var(--color-gray)',
                marginTop: 10,
                marginBottom: 10,
                width: '100%'
              }}
            />
            <div
              style={{
                backgroundColor: 'var(--color-gray-light-60)',
                borderRadius: 10,
                padding: 16
              }}
            >
              <label style={{ display: 'flex', flexDirection: 'column', gap: 8, cursor: 'pointer' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <input
                    type='checkbox'
                    checked={includeNonAlerts}
                    onChange={e => setIncludeNonAlerts(e.target.checked)}
                    style={{
                      width: 18,
                      height: 18,
                      margin: 0,
                      flexShrink: 0,
                      accentColor: 'var(--color-black)'
                    }}
                  />
                  <span style={{ fontWeight: 700, fontSize: '1.125rem', color: 'var(--color-black)' }}>
                    Include Non-alerts
                  </span>
                </div>
                <Text color='black-light-40' style={{ fontSize: '0.9rem', margin: 0 }}>
                  Non-alerts are notifications that aren&apos;t classified as full alerts due to lower criticality. They still show the volume of information we process and may include sensitive content, so they&apos;re useful for visibility and monitoring.
                </Text>
              </label>
            </div>
          </>
        )}
      </Flex>
    </Modal>
  )
}

export default ExportModal
